/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Gender } from '../testConfiguration';

import { buildSimpleRecord } from './ecgInterpretST';

export type ECGResults = {
  HR: number;
  PR: number | null;
  QTc: number | null;
  QRS: number | null;
  ST: number[] | null; /// opcjonalnie analizujemy
  PSI: number | null;
  RMSSD: number | null;
  SDNN: number | null;
  fileReportUrl: string;
  goodQuality: boolean;
  findings: string;
  gender?: Gender; // płeć wpływa na QTc
  recordId: number; // identyfikator rekordu wyniku w bazie Wecardio
};

export type Tone = 'ok' | 'warn' | 'alert' | 'info';
export interface Status {
  tone: Tone;
  label: string;
}
export interface Eval {
  value?: string | number | null;
  status: Status;
  range?: string;
  meaning?: string;
  tips?: string;
  desc?: string;
}
export interface Tile {
  key: string;
  title: string;
  unit?: string;
  value?: string | number | null;
  range?: string;
  status: Status;
  meaning?: string;
  tips?: string;
  desc?: string;
}

const statusOK: Status = { tone: 'ok', label: 'status_ok' };
const statusWarn: Status = { tone: 'warn', label: 'status_warn' };
const statusBad: Status = { tone: 'alert', label: 'status_alert' };
const statusInfo: Status = { tone: 'info', label: 'status_info' };

type LeadName = 'V1' | 'I' | 'V2' | 'V4' | 'V6' | 'II';

const DISPLAY_ORDER: LeadName[] = ['I', 'II', 'V1', 'V2', 'V4', 'V6']; // stała kolejność

interface DetailRow {
  lead: LeadName;
  st_mV: number; // wartość w mV
  inNorm: boolean; // |ST| ≤ 0.10 mV
}

export class ECGInterpreter {
  private static evalHR(hr: number | null): Eval {
    const result: Eval = {
      status: statusInfo,
      range: 'hr_range_std',
      meaning: 'hr_meaning_std',
      desc: 'hr_desc_std',
    };
    if (!hr && hr !== 0) {
      result.status = statusInfo;
      return result;
    }
    if (hr! < 60) {
      result.status = statusBad;
      result.tips = 'hr_tips_bad60';
      return result;
    }
    if (hr! > 100) {
      result.status = statusBad;
      result.tips = 'hr_tips_bad100';
      return result;
    }
    result.status = statusOK;
    return result;
  }

  private static evalPR(pr: number | null): Eval {
    const result: Eval = {
      status: statusOK,
      range: 'pr_range',
      meaning: 'pr_meaning',
      desc: 'pr_desc',
    };
    if (!pr && pr !== 0) {
      result.status = statusInfo;
      return result;
    }
    if (pr! < 120) {
      result.status = statusBad;
      result.tips = 'pr_tips_bad120';
      return result;
    }
    if (pr! > 220) {
      result.status = statusBad;
      result.tips = 'pr_tips_bad220';
      return result;
    }
    return result;
  }

  private static evalQRS(qrs: number | null): Eval {
    const result: Eval = {
      status: statusOK,
      range: 'qrs_range',
      meaning: 'qrs_meaning',
    };
    if (!qrs && qrs !== 0) {
      result.status = statusInfo;
      return result;
    }
    if (qrs! >= 120) {
      result.status = statusBad;
      result.tips = 'qrs_tips_bad120';
      return result;
    }
    if (qrs! >= 100) {
      result.status = statusWarn;
      result.tips = 'qrs_tips_bad100';
      return result;
    }
    return result;
  }

  private static evalPSI(psi: number | null): Eval {
    const result: Eval = {
      status: statusOK,
      range: 'psi_range',
      meaning: 'psi_meaning',
    };
    if (!psi && psi !== 0) {
      result.status = statusInfo;
      return result;
    }
    if (psi! >= 80) {
      result.status = statusBad;
      result.tips = 'psi_tips_bad';
      return result;
    }
    return result;
  }

  private static evalRMSSD(rmssd: number | null): Eval {
    const result: Eval = {
      status: statusOK,
      range: 'rmssd_range',
      meaning: 'rmssd_meaning',
    };
    if (!rmssd && rmssd !== 0) {
      result.status = statusInfo;
      return result;
    }
    if (rmssd! >= 68.78) {
      result.status = statusBad;
      result.tips = 'rmssd_tips_bad_hight';
      return result;
    }
    if (rmssd! <= 15.8) {
      result.status = statusWarn;
      result.tips = 'rmssd_tips_bad_low';
      return result;
    }
    return result;
  }

  private static evalSDNN(sdnn: number | null): Eval {
    const result: Eval = {
      status: statusOK,
      range: 'sdnn_range',
      meaning: 'sdnn_meaning',
    };
    if (!sdnn && sdnn !== 0) {
      result.status = statusInfo;
      return result;
    }
    if (sdnn! <= 28.49) {
      result.status = statusBad;
      result.tips = 'sdnn_tips_bad_low';
      return result;
    }
    if (sdnn! >= 79.91) {
      result.status = statusBad;
      result.tips = 'sdnn_tips_bad_hight';
      return result;
    }
    return result;
  }
  private static evalQTc(qtc: number | null, gender?: Gender): Eval {
    const result: Eval = {
      status: statusOK,
      range: 'qtc_range',
      meaning: 'qtc_meaning',
    };
    if (!qtc && qtc !== 0) {
      result.status = statusInfo;
      return result;
    }
    const upper = gender === Gender.Female ? 470 : 450;
    const borderline = gender === Gender.Female ? 460 : 440;
    if (qtc! > upper) {
      result.status = statusBad;
      result.tips = 'qtc_tips_bad';
      return result;
    }
    if (qtc! >= borderline) {
      result.status = statusWarn;
      result.tips = 'qtc_tips_warn2';
      return result;
    }
    if (qtc! < 350) {
      result.status = statusWarn;
      result.tips = 'qtc_tips_warn1';
      return result;
    }
    return result;
  }

  private static renderDetailsListHTML(details: DetailRow[]): string {
    const byLead = new Map(details.map(d => [d.lead, d]));

    const items = DISPLAY_ORDER.map(lead => {
      const it = byLead.get(lead);
      if (!it) {
        return '';
      }
      const cls = it.inNorm ? '' : ' class="alert"';
      const val = it.st_mV.toFixed(2); // zawsze 2 miejsca, kropka
      return `<li${cls}>ST ${lead} = ${val} mV</li>`;
    }).join('');

    return `<ul class="st-list">${items}</ul>`;
  }

  private static evalST(st: number[], gender?: Gender): Eval {
    const { summary, description, details } = buildSimpleRecord(st, gender);

    const result: Eval = {
      status: statusOK,
      value: ECGInterpreter.renderDetailsListHTML(details),
      desc: 'st_desc',
      range: 'st_range',
      meaning: 'st_meaning',
      tips: description,
    };
    if (st === null || Number.isNaN(st)) {
      result.status = statusInfo;
      return result;
    }
    if (summary === 'alert') {
      result.status = statusBad;
      result.tips = description;
      return result;
    }
    if (summary === 'warn') {
      result.status = statusWarn;
      result.tips = description;
      return result;
    }
    return result;
  }

  // — API klasy —
  // eslint-disable-next-line complexity
  public static analyze(results?: ECGResults): Tile[] {
    const tiles: Tile[] = [];
    if (!results) {
      return tiles;
    }

    const gender = results.gender || Gender.Male;

    const round2 = (x: number | null) => {
      if (x) {
        return Math.round((x + Number.EPSILON) * 100) / 100;
      } else {
        return null;
      }
    };

    // Helper: dodaje prosty kafelek liczbowy; pomija null; gwarantuje status
    const addNum = (
      key: Tile['key'],
      title: string,
      unit: string,
      value: number | number[] | null | undefined,
      evalFn?: ((v: number, g?: Gender) => Eval) | null,
      gender?: Gender,
      evalArrayFn?: (v: number[], g?: Gender) => Eval,
    ) => {
      if (value === null || value === undefined) {
        return;
      }

      const evaluated = (
        evalFn && !Array.isArray(value)
          ? evalFn(value, gender)
          : evalArrayFn && Array.isArray(value)
          ? evalArrayFn(value, gender)
          : {}
      ) as Eval;
      const tile: Tile = {
        key,
        title,
        unit,
        value: typeof value === 'number' ? value : undefined,
        ...evaluated,
      };
      tiles.push(tile);
    };

    // HR / PR / QRS
    addNum('HR', 'HR', 'bpm', results.HR, ECGInterpreter.evalHR);
    // ST – tylko gdy jest tablica i ma elementy; gwarantuj status
    addNum('ST', 'ST', '', results.ST, null, gender, ECGInterpreter.evalST);
    addNum('PR', 'PR', 'ms', results.PR, ECGInterpreter.evalPR);
    addNum('QRS', 'QRS', 'ms', results.QRS, ECGInterpreter.evalQRS);
    // QTc (wymaga płci)
    addNum('QTc', 'QTc', 'ms', results.QTc, ECGInterpreter.evalQTc, gender);

    addNum(
      'PSI',
      'PSI',
      '',
      results.PSI ? Math.round(results.PSI / 100) : null,
      ECGInterpreter.evalPSI,
    );
    addNum(
      'RMSSD',
      'RMSSD',
      'ms',
      round2(results.RMSSD),
      ECGInterpreter.evalRMSSD,
    );
    addNum('SDNN', 'SDNN', 'ms', round2(results.SDNN), ECGInterpreter.evalSDNN);

    return tiles;
  }

  public static isCriticalResult(tiles: Tile[]) {
    for (const tile of tiles) {
      if (tile.status === statusBad || tile.status === statusWarn) {
        return true;
      }
    }
    return false;
  }
}
